import React, { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import axios from 'axios'

function EmployeeSurveyPage() {
  const { id } = useParams()
  const navigate = useNavigate()

  const [survey, setSurvey] = useState(null)
  const [error, setError] = useState('')
  const [message, setMessage] = useState('')
  const [answers, setAnswers] = useState({})

  useEffect(() => {
    async function loadSurvey() {
      try {
        const res = await axios.get(
          `/survey/surveys/${id}`,
          { withCredentials: false } // öffentlich abrufen, keine Session mitsenden
        )
        setSurvey(res.data)
      } catch (err) {
        setError(err.response?.data || 'Fehler beim Laden der Umfrage')
      }
    }
    loadSurvey()
  }, [id])

  function handleAnswerChange(questionId, value) {
    setAnswers(prev => ({
      ...prev,
      [questionId]: value
    }))
  }

  async function handleSubmit(e) {
    e.preventDefault()
    setError('')
    setMessage('')

    try {
      const body = {
        // kein user_id mehr senden; anonym oder über Session-User wird serverseitig entschieden
        answers
      }
      await axios.post(
        `/survey/surveys/${id}/responses`,
        body,
        { withCredentials: false } // wichtig: keine Cookies/Session mitsenden
      )
      setMessage('Antwort gespeichert.')
    } catch (err) {
      if (err?.response?.status === 409) {
        setMessage('Diese Umfrage wurde bereits eingereicht.')
        return
      }
      setError(err.response?.data || 'Fehler beim Speichern')
    }
  }

  if (error) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center">
        <div className="bg-gray-800 p-8 rounded-2xl shadow-xl max-w-xl w-full">
          <p className="text-red-400 mb-6">{String(error)}</p>
          <button
            onClick={() => navigate('/')}
            className="bg-mentixxgreen hover:bg-green-400 text-black font-bold px-6 py-3 rounded-2xl transition"
          >
            Zur Startseite
          </button>
        </div>
      </div>
    )
  }

  if (!survey) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-black text-white">
        <div className="text-lg">Lade Umfrage…</div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-black text-white p-4 flex items-center justify-center">
      <div className="max-w-3xl w-full mx-auto bg-gray-800 rounded-2xl shadow-lg p-8">
        <h2 className="text-3xl font-bold mb-2">{survey.title}</h2>
        {survey.description && (
          <p className="mb-6 text-lg text-gray-300">{survey.description}</p>
        )}

        {message && <p className="text-green-400 font-semibold mb-6">{message}</p>}

        <form onSubmit={handleSubmit} className="space-y-8">
          {survey.questions.map((q, idx) => (
            <div key={q.question_id} className="bg-gray-700 p-6 rounded-xl shadow mb-2">
              <label className="block font-bold text-lg mb-3">
                {idx + 1}. {q.question_text}
              </label>

              {q.question_type === 'scale_5' && (
                <div className="flex flex-wrap gap-3">
                  {[
                    'Trifft voll zu',
                    'Trifft eher zu',
                    'Neutral',
                    'Trifft eher nicht zu',
                    'Trifft gar nicht zu'
                  ].map((opt, i) => (
                    <label
                      key={i}
                      className={`flex items-center px-4 py-2 rounded-2xl cursor-pointer bg-gray-600 hover:bg-gray-500 transition ${
                        answers[q.question_id] === opt ? 'ring-2 ring-mentixxgreen' : ''
                      }`}
                    >
                      <input
                        type="radio"
                        name={`q-${q.question_id}`}
                        value={opt}
                        checked={answers[q.question_id] === opt}
                        onChange={(e) => handleAnswerChange(q.question_id, e.target.value)}
                        className="accent-mentixxgreen mr-2"
                      />
                      {opt}
                    </label>
                  ))}
                </div>
              )}

              {q.question_type === 'open' && (
                <textarea
                  rows={3}
                  className="w-full bg-gray-800 border border-gray-600 rounded-xl text-white p-3 mt-2"
                  placeholder="Antwort eingeben…"
                  value={answers[q.question_id] || ''}
                  onChange={(e) => handleAnswerChange(q.question_id, e.target.value)}
                />
              )}

              {q.question_type === 'checkbox' && (
                <label className="flex items-center gap-2 mt-1">
                  <input
                    type="checkbox"
                    checked={answers[q.question_id] === 'Ja'}
                    onChange={(e) => {
                      const val = e.target.checked ? 'Ja' : 'Nein'
                      handleAnswerChange(q.question_id, val)
                    }}
                    className="accent-mentixxgreen"
                  />
                  Option
                </label>
              )}

              {q.question_type === 'radio' &&
               q.question_text.includes('Mentee / Mentor:in / gar nicht') && (
                <div className="flex gap-6 mt-2">
                  {['Mentee', 'Mentor:in', 'Gar nicht'].map(opt => (
                    <label key={opt} className="flex items-center px-4 py-2 rounded-2xl cursor-pointer bg-gray-600 hover:bg-gray-500 transition">
                      <input
                        type="radio"
                        name={`q-${q.question_id}`}
                        value={opt}
                        checked={answers[q.question_id] === opt}
                        onChange={(e) => handleAnswerChange(q.question_id, e.target.value)}
                        className="accent-mentixxgreen mr-2"
                      />
                      {opt}
                    </label>
                  ))}
                </div>
              )}

              {q.question_type === 'radio' &&
               !q.question_text.includes('Mentee / Mentor:in / gar nicht') && (
                <div className="flex gap-6 mt-2">
                  {['Ja', 'Nein'].map(opt => (
                    <label key={opt} className="flex items-center px-4 py-2 rounded-2xl cursor-pointer bg-gray-600 hover:bg-gray-500 transition">
                      <input
                        type="radio"
                        name={`q-${q.question_id}`}
                        value={opt}
                        checked={answers[q.question_id] === opt}
                        onChange={(e) => handleAnswerChange(q.question_id, e.target.value)}
                        className="accent-mentixxgreen mr-2"
                      />
                      {opt}
                    </label>
                  ))}
                </div>
              )}
            </div>
          ))}

          <div className="flex flex-col items-center gap-4 mt-10">
            <button
              type="submit"
              className="bg-mentixxgreen hover:bg-green-400 text-black font-bold py-3 px-12 rounded-2xl transition text-lg"
            >
              Abschicken
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default EmployeeSurveyPage
